import { Clock, HardDrive, Layers2, ListMusic, Timer } from "lucide-react";

import { Badge, Tooltip } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import {
  formatByteSize,
  formatCatalogDate,
  sumTrackDuration,
} from "@/music-catalog/utils/catalog-view";
import { useDisplaySettingsStore } from "@/settings/store/use-display-settings-store";
import { formatAlbumLength } from "@/settings/utils/format-album-length";

import type { LibraryProject } from "@/cloud/types/types";
import type { ReactNode } from "react";

/**
 * One fact about a project: an icon, a value, and the phrase that says what it
 * is.
 *
 * The phrase is set once and used twice — as the tooltip a pointer reveals and
 * as the badge's accessible name. Written separately they drift, and the half
 * that drifts silently is the one nobody sees: an icon means nothing to a
 * screen reader, so a badge without it says "17" and explains nothing.
 */
const MetaBadge = ({
  icon,
  label,
  tone = "neutral",
  value,
}: {
  icon: ReactNode;
  label: string;
  tone?: "neutral" | "warning";
  value: ReactNode;
}) => (
  <Tooltip content={label} side="top">
    {/* A neutral fact is set inline rather than as a pill, so the row starts on
        the title's edge; a warning keeps its pill and never comes first. */}
    <Badge tone={tone} variant={tone === "neutral" ? "inline" : "pill"} aria-label={label}>
      {icon}
      {value}
    </Badge>
  </Tooltip>
);

/**
 * What is true of a project, as a row of badges rather than a sentence.
 *
 * The icon carries the meaning and the badge carries the value, so the words
 * "Tracks:" and "Updated" are not repeated on every card in a grid of fourteen.
 * Nothing is lost by it: the words are still there, on hover and to assistive
 * technology alike.
 */
export const ProjectMetaBadges = ({
  duplicateCount,
  project,
}: {
  duplicateCount: number;
  project: LibraryProject;
}) => {
  const { language, t } = useTranslation();
  const albumLengthFormat = useDisplaySettingsStore((state) => state.albumLengthFormat);
  const trackCount = project.trackCount ?? project.tracks.length;
  const length = sumTrackDuration(project.tracks);
  const lengthText = formatAlbumLength({
    format: albumLengthFormat,
    language,
    seconds: length.seconds,
  });
  const updated = formatCatalogDate(project.updatedAt, language);
  const size = formatByteSize(project.byteLength, language);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <MetaBadge
        icon={<ListMusic className="size-3" aria-hidden />}
        label={t("catalog.trackCount", { count: trackCount })}
        value={trackCount}
      />

      {length.seconds > 0 && (
        <MetaBadge
          icon={<Timer className="size-3" aria-hidden />}
          label={t("catalog.lengthOf", { duration: lengthText })}
          value={lengthText}
        />
      )}

      <MetaBadge
        icon={<HardDrive className="size-3" aria-hidden />}
        label={t("catalog.sizeOf", { size })}
        value={size}
      />

      {updated !== undefined && (
        <MetaBadge
          icon={<Clock className="size-3" aria-hidden />}
          label={t("catalog.updated", { date: updated })}
          value={updated}
        />
      )}

      {duplicateCount > 0 && (
        <MetaBadge
          icon={<Layers2 className="size-3" aria-hidden />}
          label={t("catalog.duplicateCount", { count: duplicateCount })}
          tone="warning"
          value={duplicateCount}
        />
      )}

      {project.forkedFromDesktopProjectId !== null && (
        <Tooltip content={t("project.forkedFrom")} side="top">
          <Badge tone="neutral" aria-label={t("project.forkedFrom")}>
            {t("catalog.fork")}
          </Badge>
        </Tooltip>
      )}
    </div>
  );
};
